/* A dataset's profiling runs, and two of them compared measure by measure.
 *
 * The profile tab answers "what does this table look like now"; this one answers "what got better or
 * worse between two profiles, and what should somebody do about it". Its own tab rather than a card
 * beneath the profile, because the profile tab is already a full reading of one run and a second full
 * reading below it would bury both.
 *
 * **Nothing here reads the data source.** Both halves are stored profiles, compared on the server from
 * their sketches — the strip's footer says so, and the headline's hover carries the server's own
 * `computed_from`. So choosing a different pair costs one read of Lumiq's own database and never a scan
 * of a customer's table.
 *
 * **The pair is chosen here, by run id, and checked here.** The same run on both sides, or a later run
 * with nothing before it in the list, is recognised before a request is made, so the screen says why
 * there is nothing to show rather than surfacing a refusal. A choice is held as a run id rather than a
 * position, so a new profile arriving at the top of the list does not quietly move the comparison onto
 * different runs.
 *
 * **Each state says what it is about.** A history that could not be read, a comparison that failed,
 * and two runs with nothing to compare are three different sentences, because "nothing got worse" said
 * over a failed request would report a fault in the request as good news about the data.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProfileRunSummary } from '@/shared/api/generated/catalog';
import { Button } from '@/shared/ui/Button';
import { motionAllowed } from '@/shared/ui/motion';
import { useMeasuredWidth } from '@/shared/ui/useMeasuredWidth';

import {
  HISTORY_RUNS,
  MAX_HISTORY_RUNS,
  useProfileComparison,
  useProfileHistory,
} from '../api/profiler';
import {
  defaultFilter,
  earlierAfterLaterMoved,
  instantLabel,
  resolvePair,
} from '../model/history';
import type { ColumnFilter, EarlierChoice, LaterChoice, Pair } from '../model/history';
import { ComparisonView, RecommendationsBlocked } from './ComparisonView';
import { runFacts } from './historyCopy';
import { AUTO, LATEST, RunStrip } from './RunStrip';

export interface ProfileHistoryPanelProps {
  readonly datasetId: string;
  /** Switch the page to the Profile tab, where a dataset is profiled. Absent, no button is offered. */
  readonly onGoToProfile?: (() => void) | undefined;
}

/** Below this width the column table becomes one card per column. */
const NARROW_BELOW = 720;
/** How long a column a recommendation pointed at stays highlighted. */
const HIGHLIGHT_MS = 2400;

type Block = 'same' | 'noEarlier' | 'failed';

export function ProfileHistoryPanel({
  datasetId,
  onGoToProfile,
}: ProfileHistoryPanelProps): ReactElement {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(HISTORY_RUNS);
  const history = useProfileHistory(datasetId, limit);
  const [later, setLater] = useState<string>(LATEST);
  const [earlier, setEarlier] = useState<string>(AUTO);
  const [filter, setFilter] = useState<ColumnFilter | null>(null);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [allRecommendations, setAllRecommendations] = useState(false);
  const [rootRef, width] = useMeasuredWidth<HTMLDivElement>(1000);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runs = history.data?.runs ?? [];
  const indexOf = (runId: string): number =>
    runs.findIndex((run) => run.run_id === runId);
  const laterIndex = later === LATEST ? -1 : indexOf(later);
  const earlierIndex = earlier === AUTO ? -1 : indexOf(earlier);
  const laterChoice: LaterChoice = laterIndex < 0 ? 'latest' : laterIndex;
  const earlierChoice: EarlierChoice = earlierIndex < 0 ? 'auto' : earlierIndex;
  const pair: Pair | null =
    runs.length > 1 ? resolvePair(runs.length, laterChoice, earlierChoice) : null;
  const laterRun = pair === null ? undefined : runs[pair.later];
  const earlierRun = pair === null ? undefined : runs[pair.earlier];

  const comparison = useProfileComparison(
    datasetId,
    earlierRun?.run_id ?? null,
    laterRun?.run_id ?? null,
    pair !== null &&
      pair.block === null &&
      earlierRun !== undefined &&
      laterRun !== undefined,
  );

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  /* Scroll to the column a recommendation pointed at, once it has rendered. Found by attribute inside
     this panel rather than by a selector built from the name, which a column name could break. */
  useEffect(() => {
    if (highlighted === null) return;
    const rows = rootRef.current?.querySelectorAll<HTMLElement>('[data-ph-col]') ?? [];
    const row = [...rows].find((each) => each.dataset['phCol'] === highlighted);
    if (row !== undefined && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({
        block: 'center',
        behavior: motionAllowed() ? 'smooth' : 'auto',
      });
    }
  }, [highlighted, rootRef]);

  /** A new pair is a new question: its filter, search and highlight start over. */
  const choose = (nextLater: string, nextEarlier: string): void => {
    setLater(nextLater);
    setEarlier(nextEarlier);
    setFilter(null);
    setQuery('');
    setHighlighted(null);
    setAllRecommendations(false);
  };
  const pickLater = (index: number): void => {
    const run = runs[index];
    if (run === undefined) return;
    choose(index === 0 ? LATEST : run.run_id, AUTO);
  };
  const changeLater = (value: string): void => {
    const nextIndex = value === LATEST ? 0 : Math.max(0, indexOf(value));
    const kept = earlierAfterLaterMoved(earlierChoice, nextIndex);
    choose(value, kept === 'auto' ? AUTO : earlier);
  };
  const showColumn = (column: string): void => {
    setFilter('all');
    setQuery('');
    setHighlighted(column);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setHighlighted(null);
    }, HIGHLIGHT_MS);
  };

  /* Every state renders the same root element with the same ref. The width is measured once, on
     mount, and the first render is usually the loading state: a root that only the loaded state
     carried would be measured as absent and the column table would never become cards on a phone. */
  if (history.isPending) {
    return (
      <div className="lq-ph" ref={rootRef}>
        <div
          className="lq-ph__comparing"
          role="status"
          aria-label={t('profile.history.loading')}
        >
          <div className="lq-ph__shimmer lq-ph__shimmer--strip" />
          <div className="lq-ph__shimmer lq-ph__shimmer--verdict" />
          <ShimmerTiles />
          <p className="lq-ph__sub">{t('profile.history.loading')}</p>
        </div>
      </div>
    );
  }

  if (history.isError) {
    return (
      <div className="lq-ph" ref={rootRef}>
        <div className="lq-ph__notice lq-ph__notice--alert" role="alert">
          <span className="lq-ph__glyph" data-tone="error" aria-hidden="true">
            !
          </span>
          <div className="lq-ph__noticeBody">
            <p className="lq-ph__noticeTitle">{t('profile.history.failed.title')}</p>
            <p className="lq-ph__noticeText">{t('profile.history.failed.body')}</p>
          </div>
          <Button
            onClick={() => {
              void history.refetch();
            }}
          >
            {t('profile.history.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="lq-ph" ref={rootRef}>
        <div className="lq-ph__empty">
          <span className="lq-ph__emptyDots" aria-hidden="true">
            <span className="lq-ph__emptyDot" />
            <span className="lq-ph__emptyDash" />
            <span className="lq-ph__emptyDot" />
            <span className="lq-ph__emptyDash" />
            <span className="lq-ph__emptyDot" />
          </span>
          <p className="lq-ph__emptyTitle">{t('profile.history.none.title')}</p>
          <p className="lq-ph__emptyText">{t('profile.history.none.body')}</p>
          {onGoToProfile !== undefined && (
            <Button tone="primary" onClick={onGoToProfile}>
              {t('profile.history.goToProfile')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  /* The previous pair's comparison stays on screen as placeholder data while the chosen pair loads,
     so "comparing" is a fetch for a pair the screen is not yet showing. */
  const comparing =
    pair !== null &&
    pair.block === null &&
    comparison.isFetching &&
    (comparison.isPlaceholderData || comparison.isPending);
  const block: Block | null =
    pair === null
      ? null
      : (pair.block ??
        (comparison.isError
          ? 'failed'
          : comparison.isSuccess &&
              !comparison.isPlaceholderData &&
              comparison.data === null
            ? 'noEarlier'
            : null));
  const shown = block === null ? (comparison.data ?? null) : null;

  return (
    <div className="lq-ph" ref={rootRef}>
      <RunStrip
        runs={runs}
        pair={pair}
        laterValue={laterChoice === 'latest' ? LATEST : later}
        earlierValue={earlierChoice === 'auto' ? AUTO : earlier}
        onLaterChange={changeLater}
        onEarlierChange={(value) => {
          choose(later, value);
        }}
        onPick={pickLater}
        truncated={history.data.truncated}
        limit={limit}
        onLoadMore={() => {
          setLimit(MAX_HISTORY_RUNS);
        }}
        comparing={comparing}
      />

      {pair === null && runs[0] !== undefined && (
        <SingleRun run={runs[0]} onGoToProfile={onGoToProfile} />
      )}

      {block !== null && laterRun !== undefined && (
        <>
          <Blocked
            block={block}
            run={laterRun}
            onAct={() => {
              if (block === 'same') choose(later, AUTO);
              else if (block === 'noEarlier') choose(LATEST, AUTO);
              else void comparison.refetch();
            }}
          />
          <RecommendationsBlocked failed={block === 'failed'} />
        </>
      )}

      {pair !== null && block === null && shown === null && (
        <div
          className="lq-ph__comparing"
          role="status"
          aria-label={t('profile.compare.loading')}
        >
          <div className="lq-ph__shimmer lq-ph__shimmer--verdict" />
          <ShimmerTiles />
        </div>
      )}

      {shown !== null && (
        <ComparisonView
          comparison={shown}
          filter={filter ?? defaultFilter(shown.columns.length)}
          onFilter={(next) => {
            setFilter(next);
            setHighlighted(null);
          }}
          query={query}
          onQuery={setQuery}
          highlighted={highlighted}
          onShowColumn={showColumn}
          allRecommendations={allRecommendations}
          onAllRecommendations={() => {
            setAllRecommendations(true);
          }}
          narrow={width < NARROW_BELOW}
          busy={comparing}
        />
      )}
    </div>
  );
}

function ShimmerTiles(): ReactElement {
  return (
    <div className="lq-ph__shimmerGrid">
      <div className="lq-ph__shimmerTile" />
      <div className="lq-ph__shimmerTile" />
      <div className="lq-ph__shimmerTile" />
    </div>
  );
}

function SingleRun({
  run,
  onGoToProfile,
}: {
  readonly run: ProfileRunSummary;
  readonly onGoToProfile: (() => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const facts = [
    instantLabel(run.finished_at),
    run.version_number === null
      ? null
      : t('profile.history.run.schema', { version: run.version_number }),
    runFacts(t, run),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return (
    <div className="lq-ph__empty lq-ph__empty--single">
      <p className="lq-ph__emptyTitle">{t('profile.history.one.title')}</p>
      <p className="lq-ph__emptyText">{t('profile.history.one.body')}</p>
      <p className="lq-ph__sub">{t('profile.history.one.detail', { run: facts })}</p>
      {onGoToProfile !== undefined && (
        <Button onClick={onGoToProfile}>{t('profile.history.goToProfile')}</Button>
      )}
    </div>
  );
}

function Blocked({
  block,
  run,
  onAct,
}: {
  readonly block: Block;
  readonly run: ProfileRunSummary;
  readonly onAct: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const when = instantLabel(run.finished_at);
  const copy =
    block === 'same'
      ? {
          tone: 'warn',
          glyph: '!',
          title: t('profile.compare.same.title'),
          body: t('profile.compare.same.body', { when }),
          action: t('profile.compare.same.action'),
        }
      : block === 'noEarlier'
        ? {
            tone: undefined,
            glyph: 'i',
            title: t('profile.compare.noEarlier.title'),
            body: t('profile.compare.noEarlier.body', { when }),
            action: t('profile.compare.noEarlier.action'),
          }
        : {
            tone: 'error',
            glyph: '!',
            title: t('profile.compare.failed.title'),
            body: t('profile.compare.failed.body'),
            action: t('profile.compare.failed.action'),
          };
  return (
    <div
      className={
        block === 'failed' ? 'lq-ph__notice lq-ph__notice--alert' : 'lq-ph__notice'
      }
      role={block === 'failed' ? 'alert' : 'status'}
    >
      <span className="lq-ph__glyph" data-tone={copy.tone} aria-hidden="true">
        {copy.glyph}
      </span>
      <div className="lq-ph__noticeBody">
        <p className="lq-ph__noticeTitle">{copy.title}</p>
        <p className="lq-ph__noticeText">{copy.body}</p>
      </div>
      <Button size="compact" onClick={onAct}>
        {copy.action}
      </Button>
    </div>
  );
}
